/**
 * The signalling down-channel: a Pusher-protocol subscriber, written rather than pulled in.
 *
 * Four messages of that protocol are all this needs — the handshake that yields a socket id, a subscribe
 * carrying an auth the SERVER signed, its acknowledgement, and the events. A client library brings a
 * reconnection policy, presence channels and a transport ladder this page has no use for.
 *
 * THE SOCKET ID COMES FIRST AND THE AUTH DEPENDS ON IT (`"{socket_id}:{channel}"`), so `open()` resolves
 * on the SUBSCRIPTION: a caller that signalled on the socket would signal into a channel it had not joined.
 */
export type BrokerEvent = { event: string; channel?: string; data: string };

export type BrokerOpts = {
  /** `wss://host` — the origin that answers `/app/{key}`, handed down by the backend. */
  url: string;
  key: string;
  /** Signs `"{socketId}:{channel}"` server-side. It is the room token that authorizes it, not a session. */
  auth(socketId: string): Promise<{ auth: string; channel: string }>;
  onEvent(name: string, data: unknown): void;
  onNote(text: string): void;
};

export class Broker {
  private ws: WebSocket | null = null;
  private channel = '';

  constructor(private readonly o: BrokerOpts) {}

  /** Resolves once this page is SUBSCRIBED. Rejects rather than hanging: a probe that waits forever
   *  reports nothing, and "no answer" is the one outcome a measurement must never produce silently. */
  open(timeoutMs = 15_000): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(`${this.o.url}/app/${this.o.key}?protocol=7&client=arena-link&version=1.0`);
      this.ws = ws;
      const timer = setTimeout(() => reject(new Error('the broker never answered')), timeoutMs);
      const fail = (why: string): void => {
        clearTimeout(timer);
        reject(new Error(why));
      };

      ws.onerror = () => fail('the broker socket errored');
      ws.onclose = () => fail('the broker socket closed before subscribing');
      ws.onmessage = (ev) => {
        let m: BrokerEvent;
        try {
          m = JSON.parse(String(ev.data)) as BrokerEvent;
        } catch {
          return;
        }
        if (m.event === 'pusher:connection_established') {
          const socketId = (JSON.parse(m.data) as { socket_id: string }).socket_id;
          this.o.auth(socketId).then(
            (a) => {
              this.channel = a.channel;
              ws.send(JSON.stringify({ event: 'pusher:subscribe', data: { channel: a.channel, auth: a.auth } }));
            },
            (err: unknown) => fail(`the room refused this page onto its channel — ${String(err)}`),
          );
          return;
        }
        if (m.event === 'pusher_internal:subscription_succeeded') {
          clearTimeout(timer);
          // From here a dropped socket is a note, not a rejection of a promise already settled.
          ws.onclose = () => this.o.onNote('the broker socket closed');
          ws.onerror = () => this.o.onNote('the broker socket errored');
          resolve();
          return;
        }
        if (m.event === 'pusher:error') {
          fail(`broker error — ${m.data}`);
          return;
        }
        // Everything else is somebody talking. `data` arrives as a STRING even when it is an object,
        // which is the one thing about this protocol that surprises every first reader.
        if (m.channel === this.channel) {
          try {
            this.o.onEvent(m.event, JSON.parse(m.data));
          } catch {
            this.o.onEvent(m.event, m.data);
          }
        }
      };
    });
  }

  close(): void {
    this.ws?.close();
    this.ws = null;
  }
}
